import {
  BorderShape,
  BorderType,
  ButtonPosition,
  ButtonState,
  HoverStyle,
} from '../types/fitQuizButton/ButtonState';
import { getContrastedColor } from './color.helper';

export const generateCssFromButtonState = (buttonState: ButtonState) => {
  const {
    fontColor,
    iconType,
    specialIcon,
    hangerIcon,
    shoeIcon,
    apparelIcon,
    bottomsIcon,
    jacketIcon,
    swimwearIcon,
    customIconUrl,
    desktopAdvancedSettings,
    mobileAdvancedSettings,
  } = buttonState;

  const getIconUrl = () => {
    switch (iconType) {
      case 'Special':
        return specialIcon.url;
      case 'Hanger':
        return hangerIcon.url;
      case 'Shoe':
        return shoeIcon.url;
      case 'Apparel':
        return apparelIcon.url;
      case 'Bottom':
        return bottomsIcon.url;
      case 'Jacket':
        return jacketIcon.url;
      case 'Swimwear':
        return swimwearIcon.url;
      case 'Custom':
        return customIconUrl;
      case 'None':
        return '';
    }
  };

  const borderStyle = (
    borderType: BorderType,
    borderShape: BorderShape,
    borderColor: string,
    borderRadius: number
  ) => {
    switch (borderType) {
      case 'all':
        return `
          border: 1px ${borderShape} ${borderColor};
          border-radius: ${borderRadius}px !important;
        `;
      case 'bottom':
        return `
          border-bottom: 1px ${borderShape} ${borderColor};
          border-radius: ${borderRadius}px !important;
        `;
      case 'none':
        return `border: none;`;
    }
  };

  const buttonPositionStyle = (buttonPosition: ButtonPosition) => {
    switch (buttonPosition) {
      case 'Default':
        return '';
      case 'Left':
        return `
          position: fixed;
          top: 50%;
          left: 0;
          transform: rotate(-90deg) translate(-100%);
          transform-origin: top left;
        `;
      case 'Right':
        return `
          position: fixed;
          top: 50%;
          right: 0;
          transform: rotate(-90deg) translate(100%);
          transform-origin: bottom right;
        `;
      case 'Bottom':
        return `
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);`;
    }
  };

  const hoverStyle = (
    hoverStyle: HoverStyle,
    fontColor?: string,
    hoverBackgroundColor?: string
  ) => {
    switch (hoverStyle) {
      case 'None':
        return '';
      case 'Underline':
        return `body .easysize_button:hover, body .easysize_button__returning:hover, body .easysize_button__out_of_stock:hover {
          text-decoration: underline !important;
          text-decoration-color: ${fontColor} !important;
        }`;
      case 'Background':
        return `
        body .easysize_button, body .easysize_button__returning, body .easysize_button__out_of_stock {
          transition: color 0.5s;
          transition: background 0.5s;
        }
        body .easysize_button, body .easysize_button__returning, body .easysize_button__out_of_stock {
          transition: background 0.5s;
        }
        body .easysize_button:hover, body .easysize_button__returning:hover, body .easysize_button__out_of_stock:hover {
          background: ${hoverBackgroundColor};
          color: ${getContrastedColor(
            hoverBackgroundColor ?? 'transparent'
          )} !important;
          border-color: ${hoverBackgroundColor} !important;
        }
        body .easysize_button:hover::before, body .easysize_button__returning:hover::before, body .easysize_button__out_of_stock:hover::before {
          background: ${getContrastedColor(
            hoverBackgroundColor ?? 'transparent'
          )} !important;
        }
        `;
    }
  };

  const iconScale = getIconUrl() === '' ? 0 : 1.64;
  const desktopIconSize = Math.floor(
    desktopAdvancedSettings.fontSize * iconScale
  );
  const mobileIconSize = Math.floor(
    mobileAdvancedSettings.fontSize * iconScale
  );
  const desktopMaskSize = `${desktopIconSize}px`;
  const mobileMaskSize = `${desktopIconSize}px`;
  const top = '0';
  if (iconType !== 'Custom') {
    return `
    .es-loaded #easysize_button {
      display: flex;
      align-items: center;
    }

    body.es-loaded .easysize_button__returning, body.es-loaded .easysize_button__out_of_stock {
      display: flex !important;
    }

    body .easysize_button, body .easysize_button__returning, body .easysize_button__out_of_stock {
        display: flex;
        align-items: flex-end;
        font-size: ${desktopAdvancedSettings.fontSize}px !important;
        font-weight: ${parseInt(desktopAdvancedSettings.fontWeight)} !important;
        line-height: 20px !important;
        cursor: pointer !important;
        margin: ${desktopAdvancedSettings.margin.top ?? 0}px ${
      desktopAdvancedSettings.margin.horizontal ?? 0
    }px ${desktopAdvancedSettings.margin.bottom ?? 0}px !important;
        color:${fontColor};
        ${borderStyle(
          desktopAdvancedSettings.borderType,
          desktopAdvancedSettings.borderShape,
          desktopAdvancedSettings.borderColor,
          desktopAdvancedSettings.borderRadius
        )}
        padding: ${desktopAdvancedSettings.padding.vertical}px ${
      desktopAdvancedSettings.padding.horizontal
    }px !important;
        text-decoration: ${
          desktopAdvancedSettings.underline ? 'underline' : 'none !important'
        };
        text-transform: ${
          desktopAdvancedSettings.onlyCapitalLetters
            ? 'uppercase !important;'
            : 'none !important'
        };
        width: ${desktopAdvancedSettings.width};
        background: ${desktopAdvancedSettings.backgroundColor};
        justify-content: ${desktopAdvancedSettings.justifyContent};
        ${buttonPositionStyle(desktopAdvancedSettings.buttonPosition)}
      }
      
      ${hoverStyle(
        desktopAdvancedSettings.hoverStyle,
        fontColor,
        desktopAdvancedSettings.hoverBackgroundColor
      )}
      body .easysize_button:before {
        content: '';
        ${!getIconUrl() ? 'display: none !important;' : ''}
        -webkit-mask-image: url(${getIconUrl()});
        mask-image: url(${getIconUrl()});
        height: ${desktopMaskSize} !important;
        width: ${desktopMaskSize} !important;
        margin-right: 8px !important;
        -webkit-mask-size: ${desktopMaskSize};
        mask-size: ${desktopMaskSize} auto;
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        background: ${fontColor} !important;
        top: ${top} !important;
      }

      body .easysize_button__returning:before, body .easysize_button__out_of_stock:before {
        ${!getIconUrl() ? 'display: none !important;' : ''}
        height: ${desktopMaskSize} !important;
        width: ${desktopMaskSize} !important;
        -webkit-mask-image: ${
          iconType === 'None'
            ? ''
            : 'url(https://cdn.easysize.me/icons/Checkmark%20Solid%201.svg)'
        };
        mask-image: ${
          iconType === 'None'
            ? ''
            : 'url(https://cdn.easysize.me/icons/Checkmark%20Solid%201.svg)'
        };
        margin-right: 8px !important;
        -webkit-mask-size: ${desktopMaskSize};
        mask-size: ${desktopMaskSize} auto;
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        background-color: ${fontColor} !important;
        top: ${top} !important;
      }


      @media only screen and (max-width: 680px) {
        body .easysize_button, body .easysize_button__returning, body .easysize_button__out_of_stock {
          ${buttonPositionStyle(mobileAdvancedSettings.buttonPosition)}
          font-size: ${mobileAdvancedSettings.fontSize}px !important;
          font-weight: ${parseInt(
            mobileAdvancedSettings.fontWeight
          )} !important;
          margin: ${mobileAdvancedSettings.margin.top ?? 0}px ${
      mobileAdvancedSettings.margin.horizontal ?? 0
    }px ${mobileAdvancedSettings.margin.bottom ?? 0}px !important;
          ${borderStyle(
            mobileAdvancedSettings.borderType,
            mobileAdvancedSettings.borderShape,
            mobileAdvancedSettings.borderColor,
            mobileAdvancedSettings.borderRadius
          )}
          padding: ${mobileAdvancedSettings.padding.vertical}px ${
      mobileAdvancedSettings.padding.horizontal
    }px !important;
          text-decoration: ${
            mobileAdvancedSettings.underline ? 'underline' : 'none !important'
          };
          text-transform: ${
            mobileAdvancedSettings.onlyCapitalLetters
              ? 'uppercase !important;'
              : 'none !important'
          };
          width: ${mobileAdvancedSettings.width};
          background: ${mobileAdvancedSettings.backgroundColor};
          justify-content: ${mobileAdvancedSettings.justifyContent};
        }
        ${hoverStyle(
          mobileAdvancedSettings.hoverStyle,
          fontColor,
          mobileAdvancedSettings.hoverBackgroundColor
        )}
        
        body .easysize_button:before {
          height: ${mobileMaskSize} !important;
          width: ${mobileMaskSize} !important;
          -webkit-mask-size: ${mobileMaskSize};
          mask-size: ${mobileMaskSize} auto;
        }
  
        body .easysize_button__returning:before, body .easysize_button__out_of_stock:before {
          height: ${mobileMaskSize} !important;
          width: ${mobileMaskSize} !important;
          -webkit-mask-size: ${mobileMaskSize};
          mask-size: ${mobileMaskSize} auto;
        }
      }
    `;
  }
  return `
  .es-loaded #easysize_button {
    display: flex;
    align-items: flex-end;
  }

  body .easysize_button, body .easysize_button__returning, body .easysize_button__out_of_stock {
      ${buttonPositionStyle(desktopAdvancedSettings.buttonPosition)}
      display: flex;
      align-items: flex-end;
      font-size: ${desktopAdvancedSettings.fontSize}px !important;
      font-weight: ${parseInt(desktopAdvancedSettings.fontWeight)} !important;
      line-height: 20px !important;
      width: ${desktopAdvancedSettings.width};
      cursor: pointer !important;
      margin: ${desktopAdvancedSettings.margin.top ?? 0}px ${
    desktopAdvancedSettings.margin.horizontal ?? 0
  }px ${desktopAdvancedSettings.margin.bottom ?? 0}px !important;
      color:${fontColor};
      ${borderStyle(
        desktopAdvancedSettings.borderType,
        desktopAdvancedSettings.borderShape,
        desktopAdvancedSettings.borderColor,
        desktopAdvancedSettings.borderRadius
      )}
      padding: ${desktopAdvancedSettings.padding.vertical}px ${
    desktopAdvancedSettings.padding.horizontal
  }px !important;
      text-decoration: ${
        desktopAdvancedSettings.underline ? 'underline' : 'none !important'
      };
      text-transform: ${
        desktopAdvancedSettings.onlyCapitalLetters
          ? 'uppercase !important;'
          : 'none !important'
      };
      background: ${desktopAdvancedSettings.backgroundColor};
      justify-content: ${desktopAdvancedSettings.justifyContent};
    }
    ${hoverStyle(
      desktopAdvancedSettings.hoverStyle,
      fontColor,
      desktopAdvancedSettings.hoverBackgroundColor
    )}
    
    body .easysize_button:before {
      content: '';
        height: ${desktopMaskSize} !important;
        width: ${desktopMaskSize} !important;
        -webkit-mask: none;
        mask: none;
        background-image: url(${getIconUrl()});
        background-repeat: no-repeat;
        background-size: ${
          desktopIconSize !== 0 ? `${desktopIconSize * 0.92}px` : 0
        };
        background-color: transparent;
        background-position: 1px 0px;
        top: 0 !important;
    }

    body .easysize_button__returning:before, body .easysize_button__out_of_stock:before {
      height: ${desktopMaskSize} !important;
      width: ${desktopMaskSize} !important;
      margin-right: 8px !important;
      -webkit-mask-image: url(https://cdn.easysize.me/icons/Checkmark%20Solid%201.svg);
      
      mask-image: url(https://cdn.easysize.me/icons/Checkmark%20Solid%201.svg);
      -webkit-mask-size: ${desktopMaskSize};
      mask-size: ${desktopMaskSize} auto;
      -webkit-mask-position: center;
      mask-position: center;
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      background-color: ${fontColor} !important;
      top: ${top} !important;
    }

    @media only screen and (max-width: 680px) {
      ${buttonPositionStyle(mobileAdvancedSettings.buttonPosition)}
      body .easysize_button, body .easysize_button__returning, body .easysize_button__out_of_stock {
        font-size: ${mobileAdvancedSettings.fontSize}px !important;
        font-weight: ${parseInt(mobileAdvancedSettings.fontWeight)} !important;
        width: ${mobileAdvancedSettings.width};
        margin: ${mobileAdvancedSettings.margin.top ?? 0}px ${
    mobileAdvancedSettings.margin.horizontal ?? 0
  }px ${mobileAdvancedSettings.margin.bottom ?? 0}px !important;
        ${borderStyle(
          mobileAdvancedSettings.borderType,
          mobileAdvancedSettings.borderShape,
          mobileAdvancedSettings.borderColor,
          mobileAdvancedSettings.borderRadius
        )}
        padding: ${mobileAdvancedSettings.padding.vertical}px ${
    mobileAdvancedSettings.padding.horizontal
  }px !important;
        text-decoration: ${
          mobileAdvancedSettings.underline ? 'underline' : 'none !important'
        };
        text-transform: ${
          mobileAdvancedSettings.onlyCapitalLetters
            ? 'uppercase !important;'
            : 'none !important'
        };
        background: ${mobileAdvancedSettings.backgroundColor};
        justify-content: ${mobileAdvancedSettings.justifyContent};
      }
      
      ${hoverStyle(
        mobileAdvancedSettings.hoverStyle,
        fontColor,
        mobileAdvancedSettings.hoverBackgroundColor
      )}

      body .easysize_button:before {
          height: ${mobileMaskSize} !important;
          width: ${mobileMaskSize} !important;
          background-size: ${
            mobileIconSize !== 0 ? `${mobileIconSize * 0.92}px` : 0
          };
      }
  
      body .easysize_button__returning:before, body .easysize_button__out_of_stock:before {
        height: ${mobileMaskSize} !important;
        width: ${mobileMaskSize} !important;
        -webkit-mask-size: ${mobileMaskSize};
        mask-size: ${mobileMaskSize} auto;
      }
    }
  `;
};
